import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import React from 'react';
import { flushSync } from 'react-dom';
import { useSearchParams } from 'react-router-dom';

import { DividerWithText } from './divider-with-text';
import { useMuiSwitchableTheme } from '../theme';

// Vertical offset (px) the scroll target is nudged by to clear the sticky
// section tab bar. Shared by the click handler and the scroll-spy so they
// can't drift if the bar height changes.
const STICKY_TAB_OFFSET = 60;

/**
 * The outer card's box: shared by the container and its skeleton so the loading
 * state occupies exactly the space the loaded one will.
 * @param marginTop - Height of the app bar above the page, in px.
 * @returns The card's `sx`.
 */
const containerCardSx = (marginTop: number): SxProps<Theme> => ({
  width: '100%',
  typography: 'body1',
  // Fill everything under the app bar on mobile (the page wrapper has no
  // vertical padding there); leave the usual breathing room on desktop.
  height: {
    xs: `calc(100dvh - ${marginTop}px)`,
    // 24px, not 60 — the page wrapper contributes 8px of padding above
    // and below, so 60 was leaving a band of dead space under the card.
    md: `calc(100dvh - ${marginTop + 24}px)`,
  },
  // Edge to edge on mobile — rounded corners against a full-bleed page
  // just draw attention to the sliver of background either side.
  borderRadius: { xs: 0, md: 1 },
  // Column on mobile so the tab selector and the panel stack and the
  // panel can simply take the remaining space. Previously both were sized
  // by hand-tuned dvh subtractions that had to be kept in sync with the
  // selector's height.
  flexDirection: { xs: 'column', md: 'row' },
  flexWrap: { xs: 'nowrap', md: 'wrap' },
});

export type ScrollableTabSectionProps = {
  title: string;
  subtitle?: string;
  /** Rendered on the right of this tab's header. Page-scoped, unlike the
   *  container's footer `actions`, which apply to the whole surface. */
  action?: React.ReactNode;
  /** Drop the title/subtitle header — for content that brings its own. */
  hideHeader?: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  sections: {
    title: string;
    subtitle?: string;
    content: React.ReactNode;
    visible?: boolean;
  }[];
  /**
   * Whether this section's tab panel is the active one. The scroll-spy only
   * attaches its scroll listener when active, so the `keepMounted` inactive
   * panels don't each run a handler on every scroll. Defaults to true.
   */
  isActive?: boolean;
};

export type TabbedContainerProps = {
  title: string;
  actions?: React.ReactNode;
  tabs: {
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    sections: ScrollableTabSectionProps['sections'];
  }[];
  /**
   * Actions for the CURRENT page — rendered in the header, beside the tab's
   * title. Use this for anything scoped to one tab ("Add Tenant"); `actions`
   * above is the footer bar and is for the surface as a whole ("Save").
   */
  headerActions?: React.ReactNode;
  /**
   * Drop the header showing the active tab's title and description. For content
   * that carries its own heading — a detail view nested under a tab, say, where
   * the tab name would sit above an unrelated record title.
   */
  hideHeader?: boolean;
  /**
   * Title of the active tab, for callers whose tabs are backed by something
   * else — routes, say. Omit and the container tracks the active tab itself and
   * mirrors it into a `?tab=` query param.
   */
  value?: string;
  /**
   * Called with the newly selected tab's title. Required alongside `value`;
   * without it a controlled container can't change tab.
   */
  onChange?: (value: string) => void;
};

export const ScrollableTabSection: React.FC<ScrollableTabSectionProps> = ({
  title,
  sections,
  scrollRef,
  subtitle = '',
  action = null,
  hideHeader = false,
  isActive = true,
}) => {
  const [tab, setTab] = React.useState(0);
  const sectionRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const handleChange = (_e: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    const section = sectionRefs.current[newValue];

    if (section && scrollRef.current) {
      const newOffsetTop =
        section.offsetTop - scrollRef.current.offsetTop - STICKY_TAB_OFFSET;
      scrollRef.current.scrollTo({
        // If newValue is 0, scroll to the top
        top: newValue === 0 ? 0 : newOffsetTop,
        behavior: 'smooth',
      });
    }
  };

  // Scroll-spy: keep the active section tab in sync as the user scrolls
  // through the content, not just on click (SUP-244). Picks the last section
  // whose top has passed the sticky-tab offset. Only the active tab panel
  // attaches a listener — `keepMounted` keeps inactive panels mounted, and we
  // don't want each running a handler on every scroll.
  React.useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!isActive || !scrollEl) return undefined;

    const syncActiveTab = () => {
      let active = -1;
      for (let idx = 0; idx < sectionRefs.current.length; idx += 1) {
        const section = sectionRefs.current[idx];
        if (!section) continue;
        // Skip hidden sections. A `visible: false` section renders
        // `display: none`, so its `offsetTop` is 0 — it would always satisfy the
        // scroll test below and, because the last match wins, it would claim the
        // active tab. That left `value` pointing at a `display: none` <Tab>, which
        // MUI rejects: "The Tab with this value is not part of the document
        // layout." (Seen on Settings → Scheduling & Capacity, whose "Weighted
        // Animal Units" section is hidden unless that capacity mode is selected.)
        if (sections[idx]?.visible === false) continue;
        const top = section.offsetTop - scrollEl.offsetTop - STICKY_TAB_OFFSET;
        if (scrollEl.scrollTop >= top) active = idx;
      }
      // No visible section has scrolled past the offset yet — leave the current
      // tab alone rather than forcing it to a (possibly hidden) index 0.
      if (active === -1) return;
      setTab((prev) => (prev === active ? prev : active));
    };

    scrollEl.addEventListener('scroll', syncActiveTab, { passive: true });
    syncActiveTab();
    return () => scrollEl.removeEventListener('scroll', syncActiveTab);
    // `sections` (not just its length) — the spy reads each section's `visible`
    // flag, which flips at runtime as the form's values change.
  }, [scrollRef, sections, isActive]);

  return (
    <>
      {/* Hidden on mobile — the tab's name and description are already in the
          selector above, so this repeated them and cost a lot of vertical
          space on a screen that has none to spare. */}
      {/* `flex`, not `block` — CardHeader lays its title and action out as a
          row, so overriding display to block dropped the action underneath the
          title instead of aligning it right. */}
      {!hideHeader && (
        <CardHeader
          title={title}
          subheader={subtitle}
          action={action}
          slotProps={{
            title: { variant: 'h4' },
            subheader: { variant: 'subtitle2' },
          }}
          sx={{
            display: { xs: 'none', md: 'flex' },
            px: 2,
            py: 1,
            alignItems: 'flex-start',
            '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
          }}
        />
      )}
      <Tabs
        onChange={handleChange}
        value={tab}
        variant="scrollable"
        allowScrollButtonsMobile
        scrollButtons="auto"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          display: sections.length > 1 ? 'flex' : 'none',
        }}
      >
        {sections.map(({ title: sectionTitle, visible = true }, idx) => (
          <Tab
            key={`tab-${sectionTitle}`}
            label={sectionTitle}
            value={idx}
            // icon={tabItem.icon}
            iconPosition="start"
            sx={visible ? {} : { display: 'none' }}
          />
        ))}
      </Tabs>
      <CardContent
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 0,
          '&:last-child': { pb: { xs: 1.5, sm: 2 } },
        }}
      >
        {sections.map(({ visible = true, ...section }, index) => (
          <Box
            key={`section-${section.title}`}
            sx={{
              mb: sections.length === 1 ? 0 : 3,
              mt: 1,
              display: visible ? 'block' : 'none',
            }}
            ref={(el) => {
              sectionRefs.current[index] = el as HTMLDivElement | null;
            }}
          >
            {/* A single unnamed section is just "the tab's content" — the
                divider would only repeat the tab name above it. */}
            {!!section.title && (
              <DividerWithText text={section.title} align="left" />
            )}
            {!!section.subtitle && (
              <Typography
                variant="subtitle2"
                color="textSecondary"
                display="block"
                sx={{ mb: 2 }}
              >
                {section.subtitle}
              </Typography>
            )}
            {section.content}
          </Box>
        ))}
      </CardContent>
    </>
  );
};

export const TabbedContainer: React.FC<TabbedContainerProps> = ({
  title,
  tabs,
  actions = null,
  headerActions = null,
  hideHeader = false,
  value = undefined,
  onChange = undefined,
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const isControlled = value !== undefined;

  // Uncontrolled: `?tab=` IS the state — no local copy to drift from it. That
  // makes the active tab survive a reload, share correctly in a pasted link,
  // and follow the URL if something else changes it. Reading it once into
  // useState (as this used to) meant only the very first render honoured the
  // param, and writing it with raw history.replaceState left the router unaware
  // the URL had changed at all.
  const urlTab = searchParams.get('tab');
  const tab = isControlled
    ? value
    : (urlTab && tabs.some((t) => t.title === urlTab) && urlTab) ||
      tabs[0].title;

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (isControlled) {
      // The caller owns where this lives (a route, typically) — don't also
      // write ?tab=, or the two would fight over the URL.
      onChange?.(newValue);
    } else {
      const next = new URLSearchParams(searchParams);
      next.set('tab', newValue);
      // `replace` so flicking through tabs doesn't fill the back stack; the
      // Back button still leaves the page rather than stepping tab by tab.
      setSearchParams(next, { replace: true });
    }
    scrollRef?.current?.scrollTo({ top: 0, behavior: 'instant' });
  };

  const { marginTop } = useMuiSwitchableTheme();

  // Only the active tab renders on first paint; the rest mount straight after,
  // in a transition. Every panel stays mounted once it is — inactive tabs hold
  // form fields whose validators only run while mounted — but mounting them all
  // in the first render made a big form (processor settings) block for about a
  // second with nothing on screen to show for it.
  const [mountInactiveTabs, setMountInactiveTabs] = React.useState(false);
  React.useEffect(() => {
    React.startTransition(() => setMountInactiveTabs(true));
  }, []);

  // ...unless the form around this is submitted first. A transition yields to
  // every urgent update, so on a slow machine a few keystrokes hold it off long
  // enough to press Save while the other tabs are still unmounted — and their
  // field validators, which Formik only knows about once the field has mounted,
  // never run. So a submit that beats the transition finishes the job itself,
  // synchronously: the listener is on the form, which the event reaches before
  // it bubbles to React's root where Formik handles it, and `flushSync` also
  // runs the new fields' effects, so they have registered by the time Formik
  // validates. That pays the mount cost up front, but only for a save made in
  // that window. (A submit made in code, with no DOM event, isn't seen here.)
  React.useEffect(() => {
    if (mountInactiveTabs) return undefined;
    const form = scrollRef.current?.closest('form');
    if (!form) return undefined;

    const mountBeforeSubmit = () => {
      flushSync(() => setMountInactiveTabs(true));
    };
    form.addEventListener('submit', mountBeforeSubmit, true);
    return () => form.removeEventListener('submit', mountBeforeSubmit, true);
  }, [mountInactiveTabs]);

  return (
    <Card
      sx={containerCardSx(marginTop)}
      component={Grid}
      elevation={1}
      container
    >
      <TabContext value={tab}>
        <Grid
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            display: { xs: 'none', md: 'block' },
          }}
          size={3}
        >
          <CardHeader title={title} sx={{ textAlign: 'center' }} />
          <Divider />
          <TabList
            onChange={handleChange}
            orientation="vertical"
            variant="scrollable"
            sx={{ bgColor: 'transparent' }}
          >
            {tabs.map((tabItem) => (
              <Tab
                key={tabItem.title}
                label={tabItem.title}
                value={tabItem.title}
                // icon={tabItem.icon}
                iconPosition="start"
                sx={{ justifyContent: 'flex-end' }}
              />
            ))}
          </TabList>
        </Grid>
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
            p: 1.5,
            pb: 1,
            width: '100%',
            flexShrink: 0,
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>{title}</InputLabel>
            <Select
              value={tab}
              label={title}
              // Closed, show only the tab name — the descriptions below are for
              // choosing between tabs, and would make the collapsed control
              // several lines tall.
              renderValue={(value) => value as string}
              onChange={(e) =>
                handleChange(e as React.SyntheticEvent, e.target.value)
              }
            >
              {tabs.map((tabItem) => (
                <MenuItem
                  key={tabItem.title}
                  value={tabItem.title}
                  sx={{ display: 'block', py: 1 }}
                >
                  <Typography variant="body2">{tabItem.title}</Typography>
                  {/* The description that used to sit under the page title.
                      It's more useful here, where you're picking a tab. */}
                  {!!tabItem.subtitle && (
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ display: 'block', whiteSpace: 'normal' }}
                    >
                      {tabItem.subtitle}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* The header that would normally hold these is hidden on mobile, so
              they get their own row under the tab selector — beside it there
              wasn't room for both without squeezing the tab name. */}
          {!!headerActions && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              {headerActions}
            </Box>
          )}
        </Box>
        <Grid
          size={{ xs: 12, md: 9 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            // Take whatever the selector above leaves, rather than a second
            // hand-tuned dvh calc. minHeight:0 so the scroll box inside can
            // actually shrink — a flex child defaults to min-content and would
            // otherwise push past the card instead of scrolling.
            flex: { xs: 1, md: 'none' },
            minHeight: 0,
            height: { md: '100%' },
          }}
        >
          <Box
            sx={{
              overflowY: 'scroll',
              flex: 1,

              backgroundColor: 'background.paper',
              // Gentle fade at the bottom only. The previous gradient also
              // faded the top (`#0000` at 100%), which washed out the sticky
              // section tab bar that sits at top:0 inside this scroll box
              // (SUP-244). `0deg` runs bottom→top, so fading from transparent
              // at the bottom up to opaque by ~6% keeps the top crisp.
              WebkitMask: 'linear-gradient(0deg, #0000, #000 6%)',
            }}
            ref={scrollRef}
          >
            {tabs.map((tabItem) => (
              <TabPanel
                key={tabItem.title}
                value={tabItem.title}
                keepMounted
                // MUI lab's TabPanel ships 24px of padding, which stacked on top
                // of the CardContent padding below. Drop it on mobile and let
                // CardContent own the inset.
                sx={{ p: { xs: 0, md: 1.5 } }}
              >
                {(mountInactiveTabs || tab === tabItem.title) && (
                  <ScrollableTabSection
                    {...tabItem}
                    action={tab === tabItem.title ? headerActions : null}
                    hideHeader={hideHeader}
                    scrollRef={scrollRef}
                    isActive={tab === tabItem.title}
                  />
                )}
              </TabPanel>
            ))}
          </Box>
          {/* Only when there's something to put in it — an empty 80px bar with
              a top border reads as a broken footer, and costs a fifth of the
              screen on a phone. */}
          {!!actions && (
            <CardActions
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                height: 80,
                flexShrink: 0,
                borderTop: 1,
                borderColor: 'divider',
                p: { xs: 1, sm: 2 },
              }}
            >
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>
            </CardActions>
          )}
        </Grid>
      </TabContext>
    </Card>
  );
};

export type TabbedContainerSkeletonProps = {
  /** Draw the footer action bar. Match the container this stands in for. */
  hasActions?: boolean;
};

// Label widths for the desktop tab column, uneven so the rows read as tab
// names rather than as a table.
const SKELETON_TAB_WIDTHS = ['55%', '70%', '45%', '65%', '50%', '75%', '60%'];

/**
 * Loading state for a `TabbedContainer`, laid out like the real thing: the tab
 * column (desktop) or tab selector (mobile), then a section of form fields.
 * Replaces a pair of full-height blocks, which on mobile collapsed to one big
 * box that said nothing about what was coming.
 * @param root0 - Props.
 * @param root0.hasActions - Whether to draw the footer action bar.
 * @returns The skeleton card.
 */
export const TabbedContainerSkeleton: React.FC<
  TabbedContainerSkeletonProps
> = ({ hasActions = false }) => {
  const { marginTop } = useMuiSwitchableTheme();

  const section = (fieldCount: number) => (
    <Box>
      <Skeleton width={160} height={28} />
      <Skeleton width="60%" height={18} sx={{ mb: 1.5 }} />
      <Grid container spacing={2}>
        {Array.from({ length: fieldCount }, (_, idx) => (
          <Grid key={idx} size={{ xs: 12, sm: 6 }}>
            <Skeleton variant="rounded" height={40} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Card
      sx={containerCardSx(marginTop)}
      component={Grid}
      elevation={1}
      container
      aria-busy
    >
      {/* Desktop: the vertical tab column. */}
      <Grid
        sx={{
          borderRight: 1,
          borderColor: 'divider',
          display: { xs: 'none', md: 'block' },
        }}
        size={3}
      >
        <CardHeader
          title={<Skeleton width="50%" sx={{ mx: 'auto' }} />}
          sx={{ textAlign: 'center' }}
        />
        <Divider />
        <Stack sx={{ px: 2, py: 1 }}>
          {SKELETON_TAB_WIDTHS.map((width, idx) => (
            // Right-aligned, like the tab labels.
            <Box
              key={idx}
              sx={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Skeleton width={width} height={24} />
            </Box>
          ))}
        </Stack>
      </Grid>
      {/* Mobile: the tab selector. */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          p: 1.5,
          pb: 1,
          width: '100%',
          flexShrink: 0,
        }}
      >
        <Skeleton variant="rounded" height={40} />
      </Box>
      <Grid
        size={{ xs: 12, md: 9 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: { xs: 1, md: 'none' },
          minHeight: 0,
          height: { md: '100%' },
        }}
      >
        <Box sx={{ flex: 1, overflow: 'hidden', p: { xs: 0, md: 1.5 } }}>
          {/* The active tab's title and description — desktop only, as in the
              container itself. */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, px: 2, py: 1 }}>
            <Skeleton width="35%" height={40} />
            <Skeleton width="55%" height={20} />
          </Box>
          {/* Section tab bar. */}
          <Stack
            direction="row"
            spacing={3}
            sx={{
              px: 2,
              height: 48,
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Skeleton width={90} height={24} />
            <Skeleton width={110} height={24} />
            <Skeleton width={70} height={24} />
          </Stack>
          <Stack spacing={3} sx={{ px: { xs: 1.5, sm: 2 }, pt: 2 }}>
            {section(4)}
            {section(2)}
            {section(4)}
          </Stack>
        </Box>
        {hasActions && (
          <CardActions
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              height: 80,
              flexShrink: 0,
              borderTop: 1,
              borderColor: 'divider',
              p: { xs: 1, sm: 2 },
            }}
          >
            <Skeleton variant="rounded" width={88} height={36} />
          </CardActions>
        )}
      </Grid>
    </Card>
  );
};
